import { useQuery } from '@tanstack/react-query';
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { buildAbility } from '@tronche2tek/shared-types';
import { use, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AbilityContext } from '../ability';
import { ApiError } from '../api/errors';
import { usersControllerListPending } from '../api/generated';
import { meQueryOptions } from '../api/me';
import { pendingCountQueryKey } from '../api/pending';
import { isDenialCode } from '../auth/denial';
import { CommandPalette } from '../components/common/CommandPalette';
import { JobsTracker } from '../components/common/JobsTracker';
import { UserMenu } from '../components/common/UserMenu';
import { UserSearchTopBar } from '../components/users/UserSearchTopBar';

export const Route = createFileRoute('/_auth')({
  beforeLoad: ({ context }) => {
    const account = context.msalInstance.getActiveAccount();
    if (!account) {
      throw redirect({ to: '/login' });
    }
  },
  loader: async ({ context }) => {
    try {
      const me = await context.queryClient.query({
        ...meQueryOptions(),
        staleTime: 'static',
      });
      return {
        role: me.role,
        oid: me.oid,
        upn: me.upn ?? null,
        name: me.name ?? me.upn ?? me.oid,
      };
    } catch (err) {
      if (!(err instanceof ApiError)) {
        throw err;
      }

      // The account is authenticated but not allowed in. Keep the MSAL session:
      // there is nothing wrong with it, and clearing it only makes the operator
      // sign in again, successfully, to be refused again. Carry the reason to
      // the login page so it can say something instead of showing a bare form.
      if (isDenialCode(err.code)) {
        context.queryClient.removeQueries({ queryKey: ['me'] });
        throw redirect({ to: '/login', search: { denied: err.code } });
      }

      // A 401 rarely reaches this far now: `authorizedFetch` renews the token
      // and replays the request first, and ends the session itself if that is
      // refused too (`auth/session.ts`). What is left here is the 403 with no
      // denial code — a refusal this app does not have a page for — and the
      // same handling costs nothing if a 401 ever does arrive.
      if (err.status === 401 || err.status === 403) {
        context.msalInstance.setActiveAccount(null);
        context.queryClient.removeQueries({ queryKey: ['me'] });
        throw redirect({ to: '/login' });
      }
      throw err;
    }
  },
  component: AuthLayout,
  pendingComponent: LoadingSpinner,
});

function LoadingSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-(--bg)">
      <p className="label cursor">LOADING</p>
    </div>
  );
}

function AuthLayout() {
  const data = Route.useLoaderData();
  const ability = useMemo(() => buildAbility(data.role), [data.role]);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <AbilityContext value={ability}>
      <div className="flex min-h-screen flex-col bg-(--bg) text-(--ink)">
        <AppNav
          role={data.role}
          oid={data.oid}
          upn={data.upn}
          name={data.name}
        />
        <main className="mx-auto w-full max-w-[1280px] flex-1 px-4 py-8 md:px-8 md:py-12">
          <Outlet />
        </main>
        <StatusBar />
      </div>
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
      />
      <JobsTracker />
    </AbilityContext>
  );
}

function AppNav({
  role,
  oid,
  upn,
  name,
}: {
  role: string | null;
  oid: string;
  upn: string | null;
  name: string;
}) {
  const { t } = useTranslation();
  const ability = use(AbilityContext);
  const canSearch = ability.can('manage', 'Photo');
  const canUploadBatch = ability.can('upload', 'Batch');
  const canModerate = ability.can('moderate', 'Photo');
  const canGenerateCards = ability.can('generate', 'Card');
  const isAdmin = role === 'admin';

  const { data: pending } = useQuery({
    queryKey: pendingCountQueryKey,
    queryFn: async () => {
      const { data } = await usersControllerListPending({
        throwOnError: true,
      });
      return data;
    },
    enabled: canModerate,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  const pendingCount = pending?.length ?? 0;

  return (
    <header className="sticky top-0 z-30 bg-inverse-bg text-inverse-fg">
      <div className="mx-auto flex max-w-[1280px] items-center gap-3 px-4 lg:gap-5 lg:px-10">
        <Link to="/" className="group flex shrink-0 items-center py-5">
          <span className="bracket-wrap on-dark display text-base whitespace-nowrap transition-opacity group-hover:opacity-80 xl:text-2xl">
            <span className="hidden xl:inline">TRONCHE2TEK</span>
            <span className="xl:hidden">T2T</span>
          </span>
        </Link>

        {/* Fluid between 7rem and the old fixed widths, which are now maxima:
            at 390px the search was a rigid 200px and left the nav 48px to
            show four links in. */}
        {canSearch && (
          <div className="max-w-[200px] min-w-28 flex-1 py-4 md:max-w-[260px] lg:max-w-[320px]">
            <UserSearchTopBar />
          </div>
        )}

        {/* The links are `whitespace-nowrap` and the three boxes either side
            of them are `shrink-0`, so on a narrow screen the nav had nothing
            left to give and its children simply overflowed the header: at
            390px the document scrolled 107px wider than the viewport, on
            every page. Scrolling the nav itself keeps every link reachable
            without a burger menu, and `min-w-0` is what lets the flex item
            shrink far enough for the overflow to be its own. */}
        <nav className="no-scrollbar flex min-w-0 flex-1 items-center gap-3 overflow-x-auto lg:gap-6">
          {canUploadBatch && (
            <Link
              to="/operations"
              className="epi-link whitespace-nowrap"
              activeProps={{ className: 'active' }}
            >
              {t('nav.operations', 'Opérations')}
            </Link>
          )}
          {canSearch && (
            <Link
              to="/photos/kiosk"
              className="epi-link whitespace-nowrap"
              activeProps={{ className: 'active' }}
            >
              {t('nav.kiosk', 'Studio')}
            </Link>
          )}
          {canModerate && (
            <Link
              to="/photos/moderation"
              className="epi-link flex items-center gap-2 whitespace-nowrap"
              activeProps={{ className: 'active' }}
            >
              {t('nav.moderation', 'Modération')}
              {pendingCount > 0 && (
                <span className="mono bg-green px-1.5 py-0.5 text-[10px] text-ink">
                  {pendingCount}
                </span>
              )}
            </Link>
          )}
          {canGenerateCards && (
            <Link
              to="/history"
              className="epi-link whitespace-nowrap"
              activeProps={{ className: 'active' }}
            >
              {t('nav.history', 'Historique')}
            </Link>
          )}
        </nav>

        <div className="flex shrink-0 items-center gap-4 py-4">
          <UserMenu
            oid={oid}
            displayName={name}
            upn={upn}
            role={role}
            isAdmin={isAdmin}
          />
        </div>
      </div>
    </header>
  );
}

function StatusBar() {
  const { t } = useTranslation();
  return (
    <footer className="border-t border-(--rule) bg-(--bg-soft)">
      <div className="mx-auto flex max-w-[1280px] items-center justify-between px-4 py-3 md:px-8">
        <p className="label dash">{t('shell.footerLabel')}</p>
        <p className="label">{t('shell.footerMeta')}</p>
      </div>
    </footer>
  );
}
